import React from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from 'react-query';
import { useNearContext } from '../contexts/NearContext.jsx';
import { useUserWagers, getWagerIdentifier } from '../hooks/useWagers.js';
import LoadingSpinner from './LoadingSpinner.jsx';

const MyWagers = () => {
  const { isSignedIn, accountId, contractApi, formatNearAmount, nearService } = useNearContext();

  const userStatsQuery = useQuery(
    ['user-stats', accountId],
    () => contractApi.getUserStats(accountId),
    {
      enabled: Boolean(accountId) && Boolean(contractApi),
      staleTime: 60_000,
      refetchOnWindowFocus: false,
    }
  );

  const {
    data: myWagers = [],
    isLoading,
    isError,
    refetch,
  } = useUserWagers({ accountId });

  if (!isSignedIn) {
    return (
      <div className="max-w-3xl mx-auto">
        <h1 className="text-3xl font-bold text-gradient mb-6">My Wagers</h1>
        <div className="card bg-blue-50 border border-blue-200 text-blue-800">
          Connect your NEAR wallet to see the wagers you&apos;ve created or joined.
        </div>
      </div>
    );
  }

  const stats = {
    wagers_created: 0,
    wagers_joined: 0,
    wagers_won: 0,
    total_winnings: '0',
    ...userStatsQuery.data,
  };
  const joinedCount = stats.wagers_joined;
  const createdCount = stats.wagers_created;
  const totalWins = stats.wagers_won;
  const totalWinnings = formatNearAmount(stats.total_winnings || '0');

  const toNearNumber = (amount) => {
    if (!amount) return 0;
    try {
      const plain = nearService.formatNearAmount(amount);
      const parsed = Number.parseFloat(plain);
      return Number.isNaN(parsed) ? 0 : parsed;
    } catch (error) {
      console.warn('Failed to parse NEAR amount:', error);
      return 0;
    }
  };

  return (
    <div className="max-w-6xl mx-auto space-y-8">
      <header>
        <h1 className="text-3xl font-bold text-gradient mb-2">My Wagers</h1>
        <p className="text-gray-600">
          Track wagers you&apos;ve created or joined. Need a fresh matchup?{' '}
          <Link to="/create" className="text-near-blue font-semibold">
            Create one here
          </Link>
          .
        </p>
      </header>

      <section className="grid gap-4 sm:grid-cols-4">
        <div className="card bg-near-blue/10 border-near-blue/30">
          <div className="text-sm text-gray-500">Created</div>
          <div className="text-2xl font-semibold text-near-blue">{createdCount}</div>
        </div>
        <div className="card bg-near-purple/10 border-near-purple/30">
          <div className="text-sm text-gray-500">Joined</div>
          <div className="text-2xl font-semibold text-near-purple">{joinedCount}</div>
        </div>
        <div className="card bg-near-green/10 border-near-green/30">
          <div className="text-sm text-gray-500">Wagers won</div>
          <div className="text-2xl font-semibold text-near-green">{totalWins}</div>
        </div>
        <div className="card bg-yellow-100/50 border-yellow-200/80">
          <div className="text-sm text-gray-500">Total winnings</div>
          <div className="text-2xl font-semibold text-yellow-700">{totalWinnings} NEAR</div>
        </div>
      </section>

      <section className="card">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold">Your wagers</h2>
          <button type="button" className="btn-secondary text-sm" onClick={refetch}>
            Refresh
          </button>
        </div>

        {(isLoading || userStatsQuery.isFetching) && (
          <div className="flex justify-center py-10">
            <LoadingSpinner />
          </div>
        )}

        {isError && (
          <div className="text-red-500 text-sm">
            We had trouble loading your wagers. Try again in a moment.
          </div>
        )}

        {!isLoading && !isError && myWagers.length === 0 && (
          <div className="text-center text-gray-500 py-16">
            <p className="font-medium">You haven&apos;t joined any wagers yet.</p>
            <p className="text-sm mt-2">Browse the home page to see what&apos;s live, or start a fresh wager.</p>
          </div>
        )}

        {!isLoading && !isError && myWagers.length > 0 && (
          <div className="space-y-4">
            {myWagers.map((wager) => {
              const wagerId = getWagerIdentifier(wager);
              const totalStake = formatNearAmount(wager.total_stake || '0');
              const myParticipant = Array.isArray(wager.participants)
                ? wager.participants.find((participant) =>
                    typeof participant === 'string'
                      ? participant === accountId
                      : participant?.account_id === accountId
                  )
                : null;

              const myStake = myParticipant?.stake
                ? formatNearAmount(myParticipant.stake)
                : '0';
              const myWinnings = myParticipant?.winnings
                ? formatNearAmount(myParticipant.winnings)
                : '0';

              const pendingClaim = Boolean(
                wager.status === 'Resolved' &&
                  myParticipant &&
                  toNearNumber(myParticipant.winnings) > 0 &&
                  !myParticipant.claimed_winnings
              );

              return (
                <article key={wagerId} className="border border-gray-100 rounded-lg p-4 flex flex-col gap-3">
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <div>
                      <h3 className="text-lg font-semibold text-gray-800">{wager.request?.title}</h3>
                      <p className="text-sm text-gray-500">Status: {wager.status}</p>
                    </div>
                    <Link to={`/wager/${wagerId}`} className="btn-primary text-sm">
                      Manage
                    </Link>
                  </div>
                  <div className="grid gap-3 sm:grid-cols-3 text-sm">
                    <div className="bg-gray-50 rounded-lg p-3">
                      <div className="text-gray-500">Total pool</div>
                      <div className="font-semibold text-gray-800">{totalStake} NEAR</div>
                    </div>
                    <div className="bg-gray-50 rounded-lg p-3">
                      <div className="text-gray-500">My stake</div>
                      <div className="font-semibold text-gray-800">{myStake} NEAR</div>
                    </div>
                    <div className="bg-gray-50 rounded-lg p-3">
                      <div className="text-gray-500">Participants</div>
                      <div className="font-semibold text-gray-800">
                        {Array.isArray(wager.participants) ? wager.participants.length : 0}
                      </div>
                    </div>
                  </div>
                  {pendingClaim && (
                    <div className="text-sm text-amber-600 font-semibold">
                      Unclaimed winnings: {myWinnings} NEAR
                    </div>
                  )}
                </article>
              );
            })}
          </div>
        )}
      </section>
    </div>
  );
};

export default MyWagers;
